import { Component } from 'react';
import {connect} from 'react-redux';

import config from '../../config';
import merge from 'lodash.merge';
import moment from 'moment';
import './Inspection.css';

import { ucfirst, getStyleValue, scrollToElement, fetchJson, postJson, convertDMYtoYMD, isValidYear } from '../Helpers/Helpers';

import SideMenu from '../SideMenu/SideMenu';

import OtherModal from '../OtherModal/OtherModal';
import InspectionPart from './Components/InspectionPart';
import Installation from './Components/Installation';
import Maintenance from './Components/Maintenance';
import FinishInspection from './Components/FinishInspection';
import LacksOverview from './Components/LacksOverview.jsx';

import ErrorOutline from '@mui/icons-material/ErrorOutline';

import { progressIndicator } from '../TopMenu/TopMenu';

import { Button, Snackbar } from '@mui/material';

function hasOwnProperty (object, key) {
	return Object.prototype.hasOwnProperty.call(object, key);
}

class Inspection extends Component {
	constructor(props) {
		super(props);

		this.state = {
			id: props.match.params.id,
			activeView: decodeURIComponent(props.match.params.group) || 'installation',
			activeGroup: null,
			errorElements: [],
			inspection: {},
			inspectionData: {},
			menuItems: [],
			groupCodes: {
				[props.t.menu_installation]: 'Installation',
				[props.t.menu_maintenance]: 'Maintenance',
				[props.t.lacks_overview || 't.lacks_overview']: 'LacksOverview',
				[props.t.menu_finish_inspection]: 'FinishInspection',
			},
			isSending: false,
			snackbarOpen: false,
			snackbarStatus: '',
			validInstallation: false,
			validInspection: true,
			fullWidth: false,
		};

		// If URL changes, activeView is retrieved not from routing (props.match.params.group)
		// but directly from URL parts
		this.historyListener = props.history.listen((location) => {
			const urlParams = location.pathname.split('/');
			const activeView = decodeURIComponent(urlParams[urlParams.length - 1]);
			const scrollUpAfterViewChanged = () => setTimeout(() => this.scrollToTop(), 0);

			this.setState({
				activeView,
				activeGroup: this.state.groupCodes[activeView],
			}, scrollUpAfterViewChanged);
		});

	}
	componentDidMount () {
		this.scrollToTop();

		fetchJson(`inspections/${this.state.id}`)
			.then(response => {
				if (response.data) {
					this.setState({ inspection: response.data }, () => {
						this.setMenuItems(response.data.groups);
					});
				} else {
					throw new Error(this.props.t.inspection_error_cache);
				}
			}).catch(err => {
				console.error(err);
				this.snackbarError(err.message);
			});
	}
	componentWillUnmount () {
		this.historyListener();
	}
	scrollToTop () {
		if (!this.props.location.hash) {
			window.scrollTo(0, 0);
		}
	}
	setMenuItems (groups) {
		const menuItems = [
			{ name: this.props.t.menu_installation },
			{ name: this.props.t.menu_maintenance },
			{ name: 'Divider' },
		];

		Object.values(groups).forEach(({name, code, parts}) => {
			menuItems.push({ name, parts });
			this.state.groupCodes[name] = code;
		});

		menuItems.push(
			{ name: 'Divider' },
			{ name: this.props.t.lacks_overview || 't.lacks_overview' },
			{ name: 'Divider' },
			{ name: this.props.t.menu_finish_inspection },
		);
		this.setState({
			menuItems,
			groupCodes: this.state.groupCodes,
			activeGroup: this.state.groupCodes[this.state.activeView],
		});
	}
	goToNextView () {
		const { id, title } = this.props.match.params;
		const viewsInOrder = this.state.menuItems
			.filter(view => view.name !== 'Divider') // Remove 'Divider' menu items
			.map(view => view.name); // Map array of object into an array of strings with [ view.name, view.name, ...]
		const currentViewIndex = viewsInOrder.indexOf(this.state.activeView);
		let nextViewIndex = 0;

		if (currentViewIndex > -1) {
			nextViewIndex = currentViewIndex + 1;
			if (nextViewIndex < viewsInOrder.length) {
				const activeView = encodeURIComponent(viewsInOrder[nextViewIndex]);
				this.setState({ activeView });
				this.props.history.push(`/inspection/${id}/${title}/${activeView}`);
			}
		}

		// Always store inspection, even if next view doesn’t show up: users expect
		// their data to be safe even if something doesn’t seem to work
		// Redirect to overview if there are no more views (meaning, user finished inspection)
		this.storeInspection(id, nextViewIndex >= viewsInOrder.length);
	}
	storeInspection (id, redirectToOverview = false) {
		if (!this.state.validInstallation) {
			this.setState({
				snackbarOpen: true,
				snackbarStatus: this.props.t.inspection_status_fields,
			});
		} else if (!this.state.validInspection) {
			this.setState({
				snackbarOpen: true,
				snackbarStatus: this.props.t.inspection_status_scores,
			});
		} else {
			this.setState({
				isSending: true,
			});
			const loadingTimeout = setTimeout(() => { // Start showing snackbar message after a while, to prevent it flashing in and out without user being able to read if postJson is too fast
				this.setState({
					snackbarOpen: true,
					snackbarStatus: <div>{progressIndicator} {this.props.t.inspection_status_saving}</div>,
				});
			}, 2000);
			postJson(this.state.inspectionData, `inspections/${id}`)
				.then(response => {
					this.clearSnackbar(loadingTimeout);
					if (response.ok && redirectToOverview) { // 200 or 204 and redirect? then ignore any messages
						this.props.history.push('/');
					} else if (response.status !== 204) {
						return response.json();
					}
					return {};
				})
				.then(json => { // can be an OK response, or an error; either way, if it has a message, throw error to show snackbar
					if (json.message) {
						throw new Error(json.message);
					}
				})
				.catch(err => {
					this.clearSnackbar(loadingTimeout);
					this.snackbarError(err.message);
				});
		}
	}
	clearSnackbar (timer) {
		clearTimeout(timer);
		this.setState({
			isSending: false,
			snackbarOpen: false,
		});
	}
	snackbarError (message) {
		const errorIcon = (
			<ErrorOutline size={30}
				style={{ verticalAlign: 'middle', marginRight: getStyleValue('spacing-half') }}
				color="inherit"
			/>
		);

		const snackbarStatus = <div>{errorIcon} {message}</div>;
		this.openSnackbar(snackbarStatus);
	}
	openSnackbar (snackbarStatus) {
		this.setState({
			snackbarOpen: true,
			snackbarStatus,
		});
	}
	handleSubmit (event) {
		event.preventDefault ? event.preventDefault() : null; // eslint-disable-line no-unused-expressions
		const form = event.target;
		this.submitForm(form, { validate: true });
	}
	submitForm (form, { validate = true, gotoNext = true }) {
		const formData = this.getFormData(form);
		const inspectionData = this.getInspectionData(formData);
		const firstErrorElement = document.getElementById(formData.errorElements[0]);

		function hasErrors () {
			return formData.errorElements.length > 0;
		}

		if (this.state.groupCodes[this.state.activeView] === 'Installation') {
			this.setState({ validInstallation: !hasErrors() });
		}

		const stateProps = {
			inspection: inspectionData,
			inspectionData,
			errorElements: [],
		};

		if (validate && hasErrors()) {
			this.setState({
				errorElements: formData.errorElements,
			}, () => scrollToElement(firstErrorElement));
		} else {
			this.setMenuItems(inspectionData.groups);
			this.setState(stateProps, () => {
				if (gotoNext) {
					this.goToNextView();
				}
			});

		}
	}
	getInspectionData (formData) {
		const id = this.state.id;
		const groupCode = this.state.groupCodes[this.state.activeView];
		const inspectionData = this.state.inspection;

		if (groupCode === 'Installation') {
			const installation = {};
			for (let key in formData.formData) {
				if (hasOwnProperty(formData.formData, key)) {
					installation[key] = {
						value: formData.formData[key],
					};
				}
			}
			inspectionData.installation.fields = merge({}, this.state.inspection.installation.fields, installation);
			inspectionData.installation.remark_text = formData.formData.remark_text;
		} else if (groupCode === 'Maintenance') {
			const numMaintenanceFields = Math.ceil(Object.keys(formData.formData).length / 2); // per maintenance object, 2 fields (date + description) are rendered; so divide those by 2 to get number objects needed
			let maintenance = [];
			for (let i = 0; i < numMaintenanceFields; i++) {
				maintenance.push({}); // create array of n objects; don’t use Array.fill since that will create references to the same empty object, leads to strange results
			}
			for (let key in formData.formData) {
				if (hasOwnProperty(formData.formData, key)) {
					const index = parseInt(key.split('-')[0], 10);
					const itemName = key.split('-')[1];

					if (maintenance[index]) {
						maintenance[index][itemName] = formData.formData[key];
					}
				}
			}
			maintenance = maintenance.filter(item => item.maintenance_date && item.maintenance_date !== ''); // clear out empty 'rows'
			inspectionData.maintenance = merge([], this.state.inspection.maintenance, maintenance);
		} else if (groupCode === 'FinishInspection') {
			let inspection = {};
			for (let key in formData.formData) {
				if (hasOwnProperty(formData.formData, key)) {
					inspection[key] = formData.formData[key];
				}
			}
			inspectionData.general_impression_description = inspection.general_impression_description;
			inspectionData.general_impression_status = inspection.general_impression_status;
			inspectionData.inspection_status_name = inspection.inspection_status_name;
			inspectionData.inspection_advised_maintenance = inspection.inspection_advised_maintenance;
			inspectionData.inspection_part_status_name = inspection.inspection_part_status_name;
			inspectionData.inspection_part_type_brand = inspection.inspection_part_type_brand;
			inspectionData.inspection_part_type_serial_number = inspection.inspection_part_type_serial_number;
			inspectionData.inspection_executed = moment(convertDMYtoYMD(inspection.inspection_executed)).format('YYYY-MM-DD');
			inspectionData.inspection_id = id;
		} else if (groupCode === 'LacksOverview') {
			// No logic needed for now, lacks are stored directly
			// in the part[].lacks property
		} else {
			// Simple alias for readability
			const data = formData.formData;

			// Create an intermediate object. formData is a very flat object that simply
			// takes form field names + their values as key/value pairs:
			// {
			//	 79_action: 'RV',
			//	 79_year: '2021',
			//	 80_action: '',
			//	 ...,
			// }
			// This reducer creates something more useful:
			//
			// {
			//	79: { action: 'rv', year: '2021' },
			//	80: { action: '' },
			// }

			const intermediateObject = Object.keys(data).reduce((collection, item) => {
				const id = item.split('_')[0];
				const property = item.slice(item.indexOf('_') + 1); // offset + 1 = start of the rest of the property
				if (collection[id]) {
					collection[id][property] = data[item];
				} else {
					collection[id] = { [property]: data[item] };
				}
				return collection;
			}, {});

			// The intermediate object is then shaped into an array of objects
			// which is consumable by the backend:
			// [
			//	 { id: 79, action: 'rv', year: '2021' },
			//	 { id: 80, action: '' },
			// ]

			const parts = Object.keys(intermediateObject).map(key => {
				return { id: key, ...intermediateObject[key] };
			});

			// ...and finally the parts are merged into the inspectionData.
			// Note that both lacks and brand/types are stored directly on
			// their 'part' in the inspectionData. They don't have form fields
			// that need to be stored on inspectionData. However, there are
			// hidden inputs in LacksInput#LackDetails that may be stored on the
			// inspection object, but they are only used to trigger validation
			// and are otherwise ignored.

			inspectionData.groups[groupCode].parts = parts.map((part, index) => {
				return merge({}, this.state.inspection.groups[groupCode].parts[index], part);
			});
		}

		this.setState({
			validInspection: !this.hasInvalidYears(inspectionData),
		});

		return inspectionData;
	}
	hasInvalidYears (inspectionData) {
		return Object.keys(inspectionData.groups)
			.some(key => inspectionData.groups[key].parts.some(part => part.modification_year && !isValidYear(part.modification_year)));
	}
	getFormData (form) {
		const formData = {};
		const namedElements = [];
		const errorElements = [];
		const formElements = form.elements;

		for (let key in formElements) {
			if (hasOwnProperty(formElements, key)) {
				const element = formElements[key];

				if (element.id) {
					// find brands and brand-serials
					// because the autocomplete component does not support
					// a 'name' attribute, we use the id
					if (element.id.endsWith('_brand') || element.id.endsWith('_serial')) {
						namedElements.push(element);
					}
				}

				if (element.hasAttribute('name')) {
					namedElements.push(element);
				}
			}
		}
		[...new Set(namedElements)].forEach((element) => {
			// regular fields
			if (element.name) {
				formData[element.name] = element.value;

				if (!element.hasAttribute('data-notrequired') && !element.value) {
					errorElements.push(element.name);
				}
				if (element.hasAttribute('data-isvalid') && element.dataset.isvalid === 'false') {
					errorElements.push(element.name);
				}
			// brand and serial
			// see comments above: brand and serial data is retreived via id,
			// not the name attribute
			} else if (element.id && element.id !== '' && element.value !== '') {
				formData[element.id] = element.value;
			}
		});
		return { formData, errorElements };
	}
	renderView () {
		let renderData, view;
		if (this.state.inspection
			&& this.state.inspection.groups
			&& this.state.activeGroup
			&& this.state.inspection.groups[this.state.activeGroup]) {
			renderData = this.state.inspection.groups[this.state.activeGroup];
		} else {
			renderData = this.state.activeGroup;
		}

		if (renderData) {
			if (typeof renderData === 'string') {
				const views = {
					Installation,
					Maintenance,
					LacksOverview,
					FinishInspection,
				};
				const ActiveView = views[renderData];
				view = <ActiveView
					inspection_part_status_name={this.state.inspection.inspection_part_status_name}
					inspection_advised_maintenance={this.state.inspection.inspection_advised_maintenance}
					general_impression_description={this.state.inspection.general_impression_description}
					general_impression_status={this.state.inspection.general_impression_status}
					execution_date={this.state.inspection.inspection_executed}
					maintenance_data={this.state.inspection.maintenance}
					installation_id={this.state.inspection.installation.id}
					data={this.state.inspection.installation.fields}
					remark_text={this.state.inspection.installation.remark_text}
					errorElements={this.state.errorElements}
					groups={this.state.inspection.groups}
					{...this.props} />;
			} else {
				view = this.generateInspectionPart(renderData.parts);
			}
		}
		return view;
	}
	generateInspectionPart (parts) {
		// Clone parts array for sorting, because we don’t want the
		// <InspectionPart /> component to mess with the source object
		// This fixes a bug on 31-1-2022 where part id’s and codes were mis-
		// aligned after rerendering a part, because of the refactoring of
		// <InspectionPart /> from class component into a functional component
		let sortedParts = [...parts];

		// Default sorting is unreliable, because our source is an object
		// Luckily, the part‘s code is a sortable string: L0106, L0110, L0125 etc
		sortedParts.sort(function (a, b) {
			if (a.code < b.code) {
				return -1;
			} else if (a.code > b.code) {
				return 1;
			}
			return 0;
		});

		return sortedParts.map((part) => (
			<InspectionPart
				key={part.id}
				errorElements={this.state.errorElements}
				part={part}
				{...this.props}
			/>
		));
	}
	handleCloseSnackbar () {
		this.setState({ snackbarOpen: false });
	}
	handleCollapse(isCollapsed) {
		this.setState({ fullWidth: isCollapsed });
	}
	render () {
		if (!this.state.inspection || !this.state.menuItems || !this.state.activeGroup) return null;

		const { inspection_planned } = this.state.inspection;
		const snackbarStyle = {
			width: '100%',
			maxWidth: 'none',
			bottom: 0,
			left: 0,
			transform: 'translateX(0)',
			borderRadius: '0px',
		};

		return (
			<div className={ `inspection ${ this.state.fullWidth ? 'inspection--fullwidth' : '' }` }>
				<SideMenu
					date={inspection_planned}
					menuItems={this.state.menuItems}
					submitForm={this.submitForm.bind(this)}
					handleCollapse={ this.handleCollapse.bind(this) }
					{...this.props}
				/>

				<div className="inspection-content">
					<OtherModal
						className="inspection-legend-action"
						buttonLabel={this.props.t.legend}
					>
						<img className="inspection-legend-image" src={`${ config.api.remoteApi }${ config.app_inspection_legend }`} alt="conditie score" />
					</OtherModal>
					<h1>{ucfirst(this.state.activeView)}</h1>
					<form
						id="inspection-form"
						onSubmit={this.handleSubmit.bind(this)}
						ref={(form) => { this.form = form; }}
						autoComplete="off"
					>
						<div className="inspection-form-content">
							{this.renderView()}

							<Button
								className="spacing-default-bottom spacing-default-left button"
								hidden={!this.state.menuItems.length}
								disabled={this.state.isSending}
								type="submit"
								color="primary"
								variant="contained"
							>
								{this.state.activeGroup === 'FinishInspection' ? this.props.t.inspection_complete : this.props.t.inspection_next}
							</Button>
						</div>
					</form>
				</div>

				<Snackbar
					open={this.state.snackbarOpen}
					autoHideDuration={5000}
					message={this.state.snackbarStatus}
					style={snackbarStyle}
					onClose={this.handleCloseSnackbar.bind(this)}
					ContentProps={{ style: snackbarStyle }}
				/>
			</div>
		);
	}
}

function mapStateToProps (state) {
	const { t } = state.store;
	return { t };
}
export default connect(mapStateToProps)(Inspection);
